import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { getActiveTarget } from '@/lib/navigation';
import { acknowledge, isChannelUnread } from '@/lib/readState';
import { sidebarChannelOrder } from '@clanfire/shared';
import { voiceManager } from '@/realtime/voice';
import { useVoice } from '@/stores/realtime';
import { useServers } from '@/stores/servers';
import { useConversations, useReadStates } from '@/stores/social';
import { useUi } from '@/stores/ui';

export interface ShortcutInfo {
  keys: string[];
  description: string;
}

export const SHORTCUTS: Array<{ group: string; items: ShortcutInfo[] }> = [
  {
    group: 'Navigation',
    items: [
      { keys: ['Ctrl', 'K'], description: 'Quick switcher — jump to any server, channel or person' },
      { keys: ['Alt', '↑ / ↓'], description: 'Previous / next channel (or DM)' },
      { keys: ['Alt', 'Shift', '↑ / ↓'], description: 'Previous / next unread channel' },
      { keys: ['Ctrl', 'Alt', '↑ / ↓'], description: 'Previous / next server' },
    ],
  },
  {
    group: 'Messages',
    items: [
      { keys: ['Esc'], description: 'Mark the current channel or DM as read' },
      { keys: ['Shift', 'Esc'], description: 'Mark the whole server as read' },
      { keys: ['↑'], description: 'Edit your last message (in an empty message box)' },
      { keys: ['Shift', 'Enter'], description: 'New line in a message' },
    ],
  },
  {
    group: 'Voice',
    items: [
      { keys: ['Ctrl', 'Shift', 'M'], description: 'Toggle mute' },
      { keys: ['Ctrl', 'Shift', 'D'], description: 'Toggle deafen' },
    ],
  },
  { group: 'Help', items: [{ keys: ['Ctrl', '/'], description: 'Show this list' }] },
];

function currentLocation() {
  const [, app, first, second] = location.pathname.split('/');
  if (app !== 'app') return { serverId: undefined, channelId: undefined, conversationId: undefined };
  if (first === '@me') return { serverId: undefined, channelId: undefined, conversationId: second };
  return { serverId: first, channelId: second, conversationId: undefined };
}

function isTyping(target: EventTarget | null) {
  const el = target as HTMLElement | null;
  return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
}

function serverUnread(serverId: string) {
  const { channels, servers } = useServers.getState();
  const reads = useReadStates.getState().channels;
  const server = servers[serverId];
  if (!server || server.me.notificationLevel === 'NOTHING') return false;
  return Object.values(channels).some((c) => c.serverId === serverId && !reads[c.id]?.muted && isChannelUnread(c, reads[c.id]?.lastReadAt, server.me.joinedAt));
}

/** App-wide keyboard shortcuts (see SHORTCUTS for the list shown to users). */
export function useGlobalShortcuts() {
  const navigate = useNavigate();

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      const mod = e.ctrlKey || e.metaKey;
      const dialogOpen = !!document.querySelector('dialog[open]');
      const ui = useUi.getState();

      // Ctrl+K — quick switcher (toggles).
      if (mod && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (ui.modal?.type === 'quickSwitcher') ui.closeModal();
        else if (!dialogOpen || ui.modal) ui.openModal({ type: 'quickSwitcher' });
        return;
      }
      // Ctrl+/ — shortcut list.
      if (mod && e.key === '/') {
        e.preventDefault();
        ui.openModal({ type: 'shortcuts' });
        return;
      }
      if (dialogOpen) return;

      // Voice.
      if (mod && e.shiftKey && !e.altKey && e.key.toLowerCase() === 'm') {
        e.preventDefault();
        voiceManager.setMuted(!useVoice.getState().muted);
        return;
      }
      if (mod && e.shiftKey && !e.altKey && e.key.toLowerCase() === 'd') {
        e.preventDefault();
        voiceManager.setDeafened(!useVoice.getState().deafened);
        return;
      }

      const { serverId, channelId, conversationId } = currentLocation();

      // Esc / Shift+Esc — mark read (the composer and popovers handle their own Esc first).
      if (e.key === 'Escape' && !mod && !e.altKey) {
        window.dispatchEvent(new CustomEvent('clanfire:mark-read'));
        if (e.shiftKey && serverId) {
          for (const c of Object.values(useServers.getState().channels)) {
            if (c.serverId === serverId && c.type === 'TEXT') acknowledge({ channelId: c.id });
          }
        } else if (getActiveTarget()) {
          if (channelId) acknowledge({ channelId });
          else if (conversationId) acknowledge({ conversationId });
        }
        return;
      }

      const vertical = e.key === 'ArrowUp' ? -1 : e.key === 'ArrowDown' ? 1 : 0;
      if (!vertical || !e.altKey) return;
      // Alt+arrows must not break word-jumping in text fields on macOS.
      if (isTyping(e.target) && navigator.platform.startsWith('Mac')) return;
      e.preventDefault();
      const state = useServers.getState();

      // Ctrl+Alt+↑/↓ — previous/next server (Home counts as the first entry).
      if (mod) {
        const entries = ['@me', ...state.order];
        const index = entries.indexOf(serverId ?? '@me');
        const next = entries[(index + vertical + entries.length) % entries.length]!;
        const last = useUi.getState().lastChannelByServer[next];
        navigate(next === '@me' ? '/app/@me' : last ? `/app/${next}/${last}` : `/app/${next}`);
        return;
      }

      if (serverId) {
        const channels = sidebarChannelOrder(
          Object.values(state.channels).filter((c) => c.serverId === serverId),
          Object.values(state.categories).filter((c) => c.serverId === serverId),
        );
        let candidates = channels;
        // Alt+Shift — only unread text channels.
        if (e.shiftKey) {
          const server = state.servers[serverId];
          const reads = useReadStates.getState().channels;
          candidates = channels.filter((c) => c.id === channelId || (server && !reads[c.id]?.muted && isChannelUnread(c, reads[c.id]?.lastReadAt, server.me.joinedAt)));
          if (candidates.length <= 1) {
            // Nothing unread here: jump to the next server that has something unread.
            const nextServer = state.order.find((id) => id !== serverId && serverUnread(id));
            if (nextServer) navigate(`/app/${nextServer}`);
            return;
          }
        }
        const index = candidates.findIndex((c) => c.id === channelId);
        const next = candidates[(index + vertical + candidates.length) % candidates.length];
        if (next) navigate(`/app/${serverId}/${next.id}`);
        return;
      }

      // In DMs: Friends page first, then conversations in sidebar order.
      const conversations = Object.values(useConversations.getState().conversations)
        .filter((c) => !e.shiftKey || c.unreadCount > 0 || c.id === conversationId)
        .sort((a, b) => (b.lastMessageAt ?? b.createdAt).localeCompare(a.lastMessageAt ?? a.createdAt));
      const entries = [...(e.shiftKey ? [] : [null]), ...conversations.map((c) => c.id)];
      const index = entries.indexOf(conversationId ?? null);
      const next = entries[(index + vertical + entries.length) % entries.length];
      navigate(next ? `/app/@me/${next}` : '/app/@me');
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [navigate]);
}
